import { useAppState } from "./useReduxState"
import { getItemList, getPageToFetch } from "../Models/Client/RemoteData"
import { FetchState, getValue } from "../Models/Client/FetchState"
import { Result } from "../Models/Utility/Result"
import { useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import {
    APICollaborationModel,
    APICollaborationParams,
    CollaboratorRole,
    CollaboratorStatus,
    compareCollaboration
} from "../Models/API/CollaboratorModel"
import { getProjectCollaborators } from "../Actions/API/Collaborator/CollaboratorActions"
import { useAppInstance } from "./useAppInstance"

export function useCollaborators(
    appID: string,
    params: APICollaborationParams
): FetchState<{
    items: APICollaborationModel[]
    total: number
}> {
    const dispatch = useDispatch()
    const entry = useAppState(state => state.api.collaborator.table[appID])
    const instanceFetch = useAppInstance(appID)
    const appInstance = getValue(instanceFetch)

    const fetchedItems = useMemo(
        () => (entry === undefined ? undefined : getItemList(entry)),
        [entry]
    )

    const ownerId = appInstance?.ownerId
    const ownerEmail = appInstance?.ownerEmail
    const ownerType = appInstance?.ownerType

    useEffect(() => {
        const page = getPageToFetch(entry, params)

        if (page >= 0) {
            dispatch(getProjectCollaborators(appID, params, page))
        }
    }, [dispatch, params, appID, entry])

    return useMemo(() => {
        if (
            ownerId === undefined ||
            ownerEmail === undefined ||
            ownerType === undefined ||
            fetchedItems === undefined ||
            entry === undefined ||
            entry.list === undefined ||
            JSON.stringify(entry.list.params) !== JSON.stringify(params)
        ) {
            return undefined
        }

        // NOTE: since collaborator list endpoint doesn't return the owner but we want to show it,
        // we merge items and apply ordering here.
        const items = fetchedItems
            .concat(
                ownerType === "USER"
                    ? [
                          {
                              accepted: "",
                              appId: appID,
                              collaboratorEmail: ownerEmail,
                              collaboratorId: ownerId,
                              collaboratorRole: CollaboratorRole.OWNER,
                              collaboratorStatus: CollaboratorStatus.ACTIVE,
                              created: "",
                              id: -1,
                              invited: "",
                              invitedEmail: ownerEmail,
                              ownerEmail,
                              ownerId,
                              role: CollaboratorRole.OWNER,
                              orgRole: null
                          }
                      ]
                    : []
            )
            .sort(compareCollaboration(params))

        return Result.ok({ items: items, total: items.length, params })
    }, [ownerId, ownerEmail, ownerType, fetchedItems, entry, params, appID])
}
